import {
  ClipboardIcon,
  ExternalLinkIcon,
  EyeIcon,
  EyeOffIcon,
} from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';
import { getTeamsUserEmail } from '../../services/teams.js';

import { useConnectorScope } from './connector-scope.js';

/**
 * Shared connect form for the Atlassian credential (Jira and Confluence use
 * the same shape). The person pastes an API token / PAT, the Atlassian base
 * URL, and — for Atlassian Cloud — the account email. Cloud
 * (`*.atlassian.net`) requires the email; Server/DC omits it, matching
 * identity's validation. Where identity manages the Atlassian API tokens
 * page, a one-click jump surfaces it.
 */
const PROVIDER_LABELS: Record<api.AtlassianProvider, string> = {
  jira: 'Jira',
  confluence: 'Confluence',
};

const ATLASSIAN_API_TOKENS_URL =
  'https://id.atlassian.com/manage-profile/security/api-tokens';

interface FormErrors {
  token?: string;
  instanceUrl?: string;
  username?: string;
  submit?: string;
}

/**
 * Client-side mirror of identity's `instance_url` rule: the value must parse
 * as an absolute URL with an http(s) scheme. The server re-validates and its
 * 400 message is surfaced verbatim on submit.
 */
function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Atlassian Cloud sites live on `*.atlassian.net`; Server/DC run on any other host. */
function isCloudUrl(value: string): boolean {
  try {
    return new URL(value).hostname.toLowerCase().endsWith('.atlassian.net');
  } catch {
    return false;
  }
}

function validate(
  token: string,
  instanceUrl: string,
  username: string,
): FormErrors {
  const errors: FormErrors = {};
  if (token.length < 8) {
    errors.token = 'API token looks too short';
  }
  if (instanceUrl.length === 0) {
    errors.instanceUrl = 'Instance URL is required';
  } else if (!isAbsoluteHttpUrl(instanceUrl)) {
    errors.instanceUrl =
      'Enter an absolute http(s) URL (e.g. https://your-org.atlassian.net)';
  } else if (
    isCloudUrl(instanceUrl) &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)
  ) {
    errors.username = 'Atlassian Cloud requires the account email';
  }
  return errors;
}

/**
 * Atlassian's token form, for Jira and Confluence. It only ever collects a
 * token: where the provider also offers a sign-in, that choice is made in the
 * connection-method dialog before this form opens, so a form that reached the
 * screen is a token the person already chose to paste.
 */
export function AtlassianCredentialsForm({
  provider,
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
}: {
  provider: api.AtlassianProvider;
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const label = PROVIDER_LABELS[provider];
  const [token, setToken] = useState('');
  const [instanceUrl, setInstanceUrl] = useState('');
  const [username, setUsername] = useState(() => getTeamsUserEmail() ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  const cloud = isCloudUrl(instanceUrl);

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setToken(e.target.value);
    if (errors.token) setErrors((es) => ({ ...es, token: undefined }));
  };

  const handleInstanceUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setInstanceUrl(e.target.value);
    if (errors.instanceUrl)
      setErrors((es) => ({ ...es, instanceUrl: undefined }));
  };

  const handleUsernameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setUsername(e.target.value);
    if (errors.username) setErrors((es) => ({ ...es, username: undefined }));
  };

  /** Reads the clipboard into the token field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    if (action.blocked()) return;
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setToken(text.trim());
      if (errors.token) setErrors((es) => ({ ...es, token: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  /** Opens the Atlassian API-tokens page in a new tab so the user can mint a token. */
  const openApiTokensPage = (): void => {
    window.open(ATLASSIAN_API_TOKENS_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const trimmedToken = token.trim();
    const trimmedUrl = instanceUrl.trim();
    const trimmedUsername = username.trim();
    const found = validate(trimmedToken, trimmedUrl, trimmedUsername);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      if (explainUnavailableActions)
        notify.error(
          Object.values(found)[0] ?? 'Complete the required connection fields.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveAtlassianCredentials(
        provider,
        {
          token: trimmedToken,
          instanceUrl: trimmedUrl,
          // Cloud requires the account email; Server/DC omits it (identity's rule).
          ...(isCloudUrl(trimmedUrl) ? { username: trimmedUsername } : {}),
        },
        workspaceId,
        details,
      );
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${provider}-instance-url`} className="text-xs">
          Instance URL
        </Label>
        <Input
          id={`${provider}-instance-url`}
          type="url"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="https://your-org.atlassian.net"
          value={instanceUrl}
          onChange={handleInstanceUrlChange}
          disabled={submitting}
          aria-invalid={Boolean(errors.instanceUrl)}
          aria-describedby={
            errors.instanceUrl
              ? `${provider}-instance-url-err`
              : `${provider}-instance-url-help`
          }
        />
        {errors.instanceUrl ? (
          <p
            id={`${provider}-instance-url-err`}
            className="text-xs text-destructive"
          >
            {errors.instanceUrl}
          </p>
        ) : (
          <p
            id={`${provider}-instance-url-help`}
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Use{' '}
            <span className="font-medium">https://your-org.atlassian.net</span>{' '}
            for Atlassian Cloud, or your Server / Data Center base URL.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${provider}-username`} className="text-xs">
          Account email
          {!cloud && (
            <span className="text-muted-foreground"> (Cloud only)</span>
          )}
        </Label>
        <Input
          id={`${provider}-username`}
          type="email"
          autoComplete="email"
          placeholder="you@your-org.com"
          value={username}
          onChange={handleUsernameChange}
          disabled={submitting}
          aria-invalid={Boolean(errors.username)}
          aria-describedby={
            errors.username
              ? `${provider}-username-err`
              : `${provider}-username-help`
          }
        />
        {errors.username ? (
          <p
            id={`${provider}-username-err`}
            className="text-xs text-destructive"
          >
            {errors.username}
          </p>
        ) : (
          <p
            id={`${provider}-username-help`}
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Required for Atlassian Cloud. Leave blank for Server / Data Center.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor={`${provider}-token`} className="text-xs">
            API token
          </Label>
          <button
            type="button"
            onClick={openApiTokensPage}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:underline"
          >
            <ExternalLinkIcon
              className="size-3"
              strokeWidth={2}
              aria-hidden="true"
            />
            Open Atlassian API tokens
          </button>
        </div>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id={`${provider}-token`}
              type={showToken ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder={`${label} API token`}
              value={token}
              onChange={handleTokenChange}
              disabled={submitting}
              aria-invalid={Boolean(errors.token)}
              aria-describedby={
                errors.token
                  ? `${provider}-token-err`
                  : `${provider}-token-help`
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              aria-label={showToken ? 'Hide token' : 'Show token'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showToken ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={!explainUnavailableActions && submitting}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.token ? (
          <p id={`${provider}-token-err`} className="text-xs text-destructive">
            {errors.token}
          </p>
        ) : (
          <p
            id={`${provider}-token-help`}
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Create a token at{' '}
            <span className="font-medium">
              id.atlassian.com → Security → API tokens
            </span>{' '}
            (Cloud), or use a personal access token (Server / Data Center).
            Paste it here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => action.cancel(onCancel)}
          disabled={!explainUnavailableActions && submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
